// Run results: victory / failure, stats, final build (rendered mech wearing it),
// cores earned and new unlocks.

import Phaser from 'phaser';
import { SCENES } from '../config/constants';
import { COLORS } from '../config/palette';
import type { MechViewApi, ResultsSceneData } from '../core/contracts';
import { save } from '../core/save';
import type { WeaponId } from '../core/types';
import { MECHS } from '../data/mechs';
import { SECTORS } from '../data/sectors';
import { MILESTONE_TEXT, reactorColorFor, unlockDef } from '../data/unlocks';
import { createMechView } from '../systems/player/MechView';
import { gridBackdrop } from '../ui/backdrops';
import { equipName, formatTime, mutationName, synergyColor, synergyName, weaponName } from '../ui/format';
import { MenuNav, Panel, TYPE, UI, countUp, createButton, createChip, footerHints, music, setMenuCursor, txt } from '../ui/kit';

export class ResultsScene extends Phaser.Scene {
  private nav!: MenuNav;
  private view: MechViewApi | null = null;

  constructor() {
    super(SCENES.RESULTS);
  }

  create(data: ResultsSceneData): void {
    setMenuCursor(this, true);
    const mech = MECHS[data.mechId];
    const accent = data.victory ? COLORS.uiGood : COLORS.threat;
    gridBackdrop(this, mech.reactorColor);
    music('results');

    txt(this, UI.W / 2, 70, data.victory ? 'VICTORY' : 'SYSTEM FAILURE', { font: 'display', size: TYPE.title, weight: 700, spacing: 12, originX: 0.5, color: accent, glow: { color: accent, blur: 18 } });
    const where = SECTORS[data.sector]?.name ?? `SECTOR ${data.sector + 1}`;
    txt(this, UI.W / 2, 150, `${mech.name}  ·  ${data.victory ? 'ALL SECTORS CLEARED' : `FELL IN ${where}`}`, { font: 'display', size: TYPE.h3, color: COLORS.uiDim, spacing: 4, originX: 0.5 });

    // Mech wearing the final build.
    try {
      const v = createMechView(this, data.mechId);
      v.setEquipment(data.build.equipment);
      v.setWeapon((data.build.weapons.find(Boolean) as WeaponId | undefined) ?? null);
      v.setReactorColor(reactorColorFor(data.mechId));
      v.setScale(3);
      v.setPosition(470, 760);
      v.setAim(-0.1);
      v.play('idle');
      this.view = v;
    } catch (err) {
      console.warn('[results] mech view unavailable', err);
    }

    // Stats.
    const s = data.stats;
    const px = 820;
    const py = 230;
    new Panel(this, px, py, 980, 470, { fill: COLORS.uiBg, fillAlpha: 0.9, accent: mech.reactorColor, ticks: true, chamfer: 18 });
    const rows: [string, string][] = [
      ['Time', formatTime(s.timeSec)],
      ['Rooms cleared', `${s.roomsCleared}`],
      ['Kills', `${s.kills}`],
      ['Elites', `${s.eliteKills}`],
      ['Bosses', `${s.bossKills}`],
      ['Orbs opened', `${s.orbsOpened}`],
      ['Scrap earned', `${s.scrapEarned}`],
      ['Damage dealt', `${Math.round(s.damageDealt)}`],
      ['Damage taken', `${Math.round(s.damageTaken)}`],
      ['Highest hit', `${Math.round(s.highestHit)}`],
    ];
    rows.forEach(([k, v], i) => {
      const col = i % 2;
      const row = Math.floor(i / 2);
      const x = px + 50 + col * 470;
      const y = py + 40 + row * 64;
      txt(this, x, y, k.toUpperCase(), { font: 'display', size: TYPE.label, color: COLORS.uiDim, spacing: 2 });
      txt(this, x + 400, y - 4, v, { font: 'display', size: TYPE.h3, weight: 700, originX: 1 });
    });

    // Build chips.
    const chips: Phaser.GameObjects.Container[] = [];
    let cx = px;
    const cy = py + 510;
    const addChip = (label: string, color: number) => {
      const c = createChip(this, cx, cy + Math.floor(chips.length / 6) * 44, label, color);
      cx += c.getBounds().width + 10;
      if (chips.length % 6 === 5) cx = px;
      chips.push(c);
    };
    for (const w of data.build.weapons) if (w) addChip(weaponName(w), COLORS.orbArsenal);
    for (const e of data.build.equipment) addChip(equipName(e), COLORS.orbArmor);
    for (const m of data.build.mutations) addChip(mutationName(m), COLORS.orbUnstable);
    for (const sy of data.build.synergies) addChip(synergyName(sy), synergyColor(sy));

    // Cores.
    const coresText = txt(this, px, py + 620, 'CORES +0', { font: 'display', size: TYPE.h2, weight: 700, color: COLORS.ultBar });
    countUp(this, coresText, 0, data.coresEarned, { duration: 1200, format: (n) => `CORES +${Math.round(n)}   ·   TOTAL ${save.data.cores}` });
    if (data.newUnlocks.length) {
      const names = data.newUnlocks.map((id) => MILESTONE_TEXT[id] ?? unlockDef(id)?.name ?? id).join('   ·   ');
      txt(this, px, py + 676, `UNLOCKED: ${names}`, { font: 'display', size: TYPE.small, color: COLORS.uiGood, spacing: 2, wrap: 980 });
    }

    this.nav = new MenuNav(this, { layout: 'horizontal' });
    const hangar = createButton(this, UI.W / 2 - 190, UI.H - 90, { label: 'Hangar', width: 340, height: 64, variant: 'primary', accent: mech.reactorColor, align: 'center', onClick: () => this.scene.start(SCENES.HANGAR) }, this.nav);
    createButton(this, UI.W / 2 + 190, UI.H - 90, { label: 'Main menu', width: 340, height: 64, align: 'center', onClick: () => this.scene.start(SCENES.MENU) }, this.nav);
    footerHints(this, [{ action: 'confirm', label: 'Select' }]);
    this.nav.focus(hangar, { silent: true });
    this.nav.lock(600);
    this.cameras.main.fadeIn(400, 5, 7, 11);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.view?.destroy();
      this.view = null;
    });
  }

  update(_t: number, delta: number): void {
    this.nav?.update();
    this.view?.update(delta / 1000);
  }
}
